import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { useLang } from "@/context/LanguageContext";
import ProductCard from "@/components/ProductCard";
import { Heart } from "lucide-react";

const Wishlist = () => {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const { productIds } = useWishlist();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (user) {
          const res = await api.get("/wishlist");
          setItems(res.data.items || []);
        } else if (productIds.length > 0) {
          // Guest: fetch each product individually
          const products = await Promise.all(
            productIds.map((id) => api.get(`/products/${id}`).then((r) => r.data).catch(() => null))
          );
          setItems(products.filter(Boolean));
        } else {
          setItems([]);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, productIds.length]);

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="aspect-[4/5] bg-[#F5F2ED] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div data-testid="wishlist-empty" className="max-w-[800px] mx-auto px-6 py-24 text-center">
        <Heart size={48} className="mx-auto mb-6 text-[#D4AF37]" strokeWidth={1.5} />
        <h1 className="font-serif text-4xl mb-4">{t("wishlist.title")}</h1>
        <p className="text-[#736B66] mb-8">{t("wishlist.empty")}</p>
        <Link to="/shop" className="luxury-btn">{t("wishlist.exploreShop")}</Link>
      </div>
    );
  }

  return (
    <div data-testid="wishlist-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
      <p className="overline mb-2">Amazypet</p>
      <h1 className="font-serif text-4xl lg:text-5xl mb-2">{t("wishlist.title")}</h1>
      <p className="text-sm text-[#736B66] mb-10">
        {items.length} {items.length === 1 ? t("wishlist.item") : t("wishlist.items")}
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-10">
        {items.map((p, i) => (
          <ProductCard key={p.id} product={p} index={i} />
        ))}
      </div>
    </div>
  );
};

export default Wishlist;
